import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Save, Check } from 'lucide-react';
import { Exercise, ExerciseUpdate, MuscleGroup } from '../types';
import { useMuscleGroups } from '../hooks/useExercises';
import FileUpload from './FileUpload';

// Схема валидации для редактирования упражнения
const editExerciseSchema = z.object({
  name: z.string().min(1, 'Название обязательно'),
  description: z.string().optional(),
  instructions: z.string().optional(),
  muscle_group_ids: z.array(z.number()).min(1, 'Выберите хотя бы одну группу мышц'),
  image_url: z.string().optional(),
  video_url: z.string().optional(),
  is_public: z.boolean().default(false),
});

type EditExerciseFormData = z.infer<typeof editExerciseSchema>;

interface EditExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: number, data: ExerciseUpdate) => void;
  exercise: Exercise | null;
  isLoading?: boolean;
}

const EditExerciseModal: React.FC<EditExerciseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  exercise,
  isLoading = false,
}) => {
  const { muscleGroups, isLoading: isMuscleGroupsLoading } = useMuscleGroups();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<EditExerciseFormData>({
    resolver: zodResolver(editExerciseSchema),
    mode: 'onChange',
    defaultValues: {
      muscle_group_ids: [],
      is_public: false,
    },
  });

  const watchedMuscleGroupIds = watch('muscle_group_ids') || [];

  const handleMuscleGroupToggle = (groupId: number) => {
    const currentIds = watchedMuscleGroupIds;
    const newIds = currentIds.includes(groupId)
      ? currentIds.filter(id => id !== groupId)
      : [...currentIds, groupId];
    
    setValue('muscle_group_ids', newIds, { shouldValidate: true });
  };

  // Заполняем форму данными упражнения при открытии
  useEffect(() => {
    if (exercise && isOpen) {
      setValue('name', exercise.name);
      setValue('description', exercise.description || '');
      setValue('instructions', exercise.instructions || '');
      setValue('muscle_group_ids', exercise.muscle_group_ids || []);
      setValue('image_url', exercise.image_url || '');
      setValue('video_url', exercise.video_url || '');
      setValue('is_public', exercise.is_public);
    }
  }, [exercise, isOpen, setValue]);

  const handleFormSubmit = (data: EditExerciseFormData) => {
    if (exercise) {
      onSubmit(exercise.id, data);
      reset();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen || !exercise) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Редактировать упражнение
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          <div className="space-y-6">
            {/* Название упражнения */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Название упражнения *
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Например: Жим штанги лежа"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Группы мышц */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Группы мышц *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {muscleGroups.map((group: MuscleGroup) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleMuscleGroupToggle(group.id)}
                    className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
                      watchedMuscleGroupIds.includes(group.id)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    } ${isMuscleGroupsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isMuscleGroupsLoading}
                  >
                    <span className="text-sm font-medium">{group.name}</span>
                    {watchedMuscleGroupIds.includes(group.id) && (
                      <Check size={16} className="text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
              {errors.muscle_group_ids && (
                <p className="mt-1 text-sm text-red-600">{errors.muscle_group_ids.message}</p>
              )}
              {isMuscleGroupsLoading && (
                <p className="mt-1 text-sm text-gray-500">Загрузка групп мышц...</p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Выберите одну или несколько групп мышц, к которым относится это упражнение
              </p>
            </div>

            {/* Описание */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Описание
              </label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Краткое описание упражнения"
              />
            </div>

            {/* Инструкции */}
            <div>
              <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-2">
                Инструкции по выполнению
              </label>
              <textarea
                {...register('instructions')}
                id="instructions"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Пошаговые инструкции по правильному выполнению упражнения"
              />
            </div>

            {/* Медиа файлы */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Изображение упражнения
                </label>
                <FileUpload
                  type="image"
                  onUpload={(fileUrl) => setValue('image_url', fileUrl, { shouldValidate: true })}
                  onRemove={() => setValue('image_url', '', { shouldValidate: true })}
                  currentUrl={watch('image_url')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Видео упражнения
                </label>
                <FileUpload
                  type="video"
                  onUpload={(fileUrl) => setValue('video_url', fileUrl, { shouldValidate: true })}
                  onRemove={() => setValue('video_url', '', { shouldValidate: true })}
                  currentUrl={watch('video_url')}
                />
              </div>
            </div>

            {/* Публичный доступ */}
            <div className="flex items-center space-x-3">
              <input
                {...register('is_public')}
                type="checkbox"
                id="is_public"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_public" className="text-sm font-medium text-gray-700">
                Сделать упражнение доступным другим тренерам
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Если включено, другие тренеры смогут видеть и использовать это упражнение в своих тренировках
            </p>

            {/* Предварительный просмотр выбранных групп мышц */}
            {watchedMuscleGroupIds.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Выбранные группы мышц:</h4>
                <div className="flex flex-wrap gap-2">
                  {watchedMuscleGroupIds.map(groupId => {
                    const group = muscleGroups.find(g => g.id === groupId);
                    return group ? (
                      <span
                        key={groupId}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {group.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Кнопки */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Сохранение...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Сохранить изменения</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExerciseModal; 